import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MainPage from './components/MainPage';
import AuthPage from './components/AuthPage';
import utils, { AUTH_EXPIRED_EVENT } from './utils/utils';
import { AuthContext } from './utils/AuthContext';
import { API } from './service/ProductsService';
import { basket } from './utils/basketStore';
import { favorites } from './utils/favoritesStore';

// האתר פתוח לכולם (חיפוש, דשבורד, רשימות). התחברות נדרשת לעדכון מחיר, פידבק ורשימת קניות:
// הקומפוננטה קוראת requireLogin(reason, action) (ר' AuthContext), ואם אין משתמש נפתח חלון AuthPage.
// טוקן שפג / משתמש שנחסם → השרת מחזיר code 90 בפעולה כזו → AUTH_EXPIRED_EVENT → מתנתקים ופותחים את חלון הכניסה.
const ZolBakfarApp = () => {
  const [user, setUser] = useState(() => utils.getCurrentUser());
  const [authPrompt, setAuthPrompt] = useState(null);   // { reason } — חלון הכניסה פתוח
  const pendingAction = useRef(null);                   // הפעולה שתרוץ אחרי התחברות מוצלחת

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      basket.reload();
      favorites.reload();
      setAuthPrompt({ reason: 'ההתחברות פגה, נא להתחבר מחדש' });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const requireLogin = useCallback((reason, action) => {
    if (utils.getCurrentUser()) {
      if (action) action();
      return;
    }
    pendingAction.current = action || null;
    setAuthPrompt({ reason });
  }, []);

  const closeAuth = useCallback(() => {
    pendingAction.current = null;
    setAuthPrompt(null);
  }, []);

  const handleLogin = (token, loggedUser) => {
    utils.saveLogin(token, loggedUser);
    setUser(utils.getCurrentUser());
    basket.reload();      // רשימת הקניות של המשתמש הזה
    favorites.reload();   // והמועדפים שלו (מהשרת)
    setAuthPrompt(null);
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) action();
  };

  const logout = useCallback(async () => {
    // מבטל את הטוקן בשרת (spSignOut); מתנתקים מקומית גם אם הקריאה נכשלה
    try { await API.signOut(); } catch (e) { /* ממשיכים בניתוק */ }
    utils.clearLogin();
    setUser(null);
    basket.reload();      // בלי משתמש — בלי רשימה
    favorites.reload();
  }, []);

  const updateUser = useCallback((patch) => {
    utils.updateCurrentUser(patch);
    setUser(utils.getCurrentUser());
  }, []);

  const auth = useMemo(() => ({ user, requireLogin, logout, updateUser }), [user, requireLogin, logout, updateUser]);

  return (
    <AuthContext.Provider value={auth}>
      <div dir="rtl" lang="he">
        <MainPage />
        {authPrompt && <AuthPage reason={authPrompt.reason} onLogin={handleLogin} onClose={closeAuth} />}
      </div>
    </AuthContext.Provider>
  );
};

export default ZolBakfarApp;
